import { useCallback, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import { ErrorState } from '@/shared/components'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast'
import type { ISensemakerOverviewProfile } from '@brushfire/types'
import {
  useDeactivateSensemaker,
  useReactivateSensemaker,
} from '../api/sensemaker-list-mutations'
import { useSensemakerOverview } from '../api/sensemaker-overview-queries'
import {
  SensemakerDeactivateModal,
  SensemakerReactivateModal,
} from '../components/SensemakerStatusModals'
import {
  SensemakerInstrumentSplitChart,
  SensemakerOverviewBreadcrumb,
  SensemakerOverviewCampaignsSection,
  SensemakerOverviewMetricTiles,
  SensemakerOverviewPageSkeleton,
  SensemakerOverviewPaymentSection,
  SensemakerOverviewProfileHeader,
  SensemakerOverviewTeamSection,
  SensemakerTokenBurnTrendChart,
} from '../components/overview'
import {
  SENSEMAKER_OVERVIEW_BREADCRUMB_TO_PROFILE_GAP_PX,
  SENSEMAKER_OVERVIEW_CHARTS_TO_CAMPAIGNS_GAP_PX,
  SENSEMAKER_OVERVIEW_CHART_GAP_PX,
  SENSEMAKER_OVERVIEW_MAJOR_SECTION_GAP_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TO_TEAM_GAP_PX,
  SENSEMAKER_OVERVIEW_METRICS_TO_CHARTS_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_TO_METRICS_GAP_PX,
} from '../lib/sensemaker-overview-layout'
import { scrollAppShellMainToElement } from '@/shared/hooks/useScrollAppShellMainToTop'
import { buildSensemakerPaymentMock } from '../lib/sensemaker-overview-payment.mock'
import { buildSensemakerTokenMock } from '../lib/sensemaker-overview-token.mock'

export function SensemakerOverviewPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const { data, isLoading, isError, refetch } = useSensemakerOverview(tenantId)
  const teamSectionRef = useRef<HTMLDivElement>(null)
  const [teamHighlight, setTeamHighlight] = useState(false)
  const [modalMode, setModalMode] = useState<'deactivate' | 'reactivate' | null>(null)
  const { showToast } = useToast()
  const deactivateMutation = useDeactivateSensemaker()
  const reactivateMutation = useReactivateSensemaker()

  useDocumentTitle(data?.profile.tenantName ?? 'Sensemaker Overview')

  const scrollToTeam = useCallback(() => {
    const section =
      teamSectionRef.current ??
      document.getElementById('sensemaker-overview-team-section')
    if (!section) return
    scrollAppShellMainToElement(section, { behavior: 'smooth', offsetTop: 24 })
    setTeamHighlight(true)
    window.setTimeout(() => setTeamHighlight(false), 2000)
  }, [])

  const closeModal = () => {
    if (deactivateMutation.isPending || reactivateMutation.isPending) return
    setModalMode(null)
  }

  const confirmStatusChange = async (profile: ISensemakerOverviewProfile) => {
    if (!modalMode || !tenantId) return
    try {
      if (modalMode === 'deactivate') {
        await deactivateMutation.mutateAsync(tenantId)
        showToast(`${profile.tenantName} deactivated`, 'success')
      } else {
        await reactivateMutation.mutateAsync(tenantId)
        showToast(`${profile.tenantName} reactivated`, 'success')
      }
      closeModal()
      await refetch()
    } catch {
      showToast('Unable to update tenant status. Please try again.', 'error')
    }
  }

  if (isLoading) {
    return <SensemakerOverviewPageSkeleton />
  }

  if (isError || !data || !tenantId) {
    return (
      <ErrorState
        message="Could not load Sensemaker overview"
        onRetry={() => void refetch()}
      />
    )
  }

  const tokenMock = buildSensemakerTokenMock(tenantId, data.profile.accountCreatedAt)
  const paymentMock = buildSensemakerPaymentMock(data.metrics.totalBilledUsd)

  return (
    <Box data-testid="sensemaker-overview-page" sx={{ display: 'flex', flexDirection: 'column' }}>
      <SensemakerOverviewBreadcrumb tenantName={data.profile.tenantName} />
      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_BREADCRUMB_TO_PROFILE_GAP_PX}px` }}>
        <SensemakerOverviewProfileHeader
          profile={data.profile}
          tokenMock={tokenMock}
          onTeamMembersClick={scrollToTeam}
          onDeactivate={() => setModalMode('deactivate')}
          onReactivate={() => setModalMode('reactivate')}
        />
      </Box>
      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_PROFILE_TO_METRICS_GAP_PX}px` }}>
        <SensemakerOverviewMetricTiles metrics={data.metrics} />
      </Box>
      <Box
        sx={{
          mt: `${SENSEMAKER_OVERVIEW_METRICS_TO_CHARTS_GAP_PX}px`,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: `${SENSEMAKER_OVERVIEW_CHART_GAP_PX}px`,
        }}
      >
        <SensemakerTokenBurnTrendChart tokenMock={tokenMock} />
        <SensemakerInstrumentSplitChart split={data.instrumentTypeSplit} />
      </Box>
      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_CHARTS_TO_CAMPAIGNS_GAP_PX}px` }}>
        <SensemakerOverviewCampaignsSection campaigns={data.campaigns} />
      </Box>
      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_MAJOR_SECTION_GAP_PX}px` }}>
        <SensemakerOverviewPaymentSection payment={paymentMock} />
      </Box>
      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_PAYMENT_TO_TEAM_GAP_PX}px` }}>
        <SensemakerOverviewTeamSection
          ref={teamSectionRef}
          members={data.teamMembers}
          highlight={teamHighlight}
        />
      </Box>

      <SensemakerDeactivateModal
        open={modalMode === 'deactivate'}
        tenantName={data.profile.tenantName}
        pending={deactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange(data.profile)}
      />
      <SensemakerReactivateModal
        open={modalMode === 'reactivate'}
        tenantName={data.profile.tenantName}
        pending={reactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange(data.profile)}
      />
    </Box>
  )
}
